import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAnimatedNavigate } from '../../../../hooks/useAnimatedNavigate';
import { 
  XMarkIcon, 
  DocumentTextIcon,
  ChartBarIcon,
  InformationCircleIcon,
  CogIcon,
  SparklesIcon,
  ArrowsUpDownIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useContacts } from '../../../contacts/hooks/useContacts';
import { useMatters } from '../../../matters/hooks/useMatters';
import { useKeycloak } from '@react-keycloak/web';
import { useUserInfo } from '../../../../hooks/useUserPermissions';
import { useDocumentAPI } from '../../DocumentAPI';
import { useDocumentCollaboration } from '../../stores/useDocumentCollaborationStore';
import { CollaborationSettingsDrawer } from '../CollaborationSettingsDrawer';
import InviteUserModal from '../InviteUserModal';


// Types
import { 
  DMSManagerProps, 
  SelectedTab,
  FilterStatus,
  Category
} from './types/documentVersion.types';

// Hooks
import { useDocumentVersions } from './hooks/useDocumentVersions';
import { useVersionActions } from './hooks/useVersionActions';

// Components
import { VersionsList } from './components/VersionsList';
import { DocumentAnalytics } from './components/DocumentAnalytics';
import { DocumentMetadata } from './components/DocumentMetadata';
import { ProcessingPanel } from './components/ProcessingPanel';
import { ComparisonView } from './components/ComparisonView';
import { WorkflowPanel } from './components/WorkflowPanel';

// Modals
import { DeleteVersionModal } from './modals/DeleteVersionModal';
import { PromoteVersionModal } from './modals/PromoteVersionModal';
import { SuccessToast } from './components/SuccessToast';

export function DMSManager({
  isOpen,
  onClose,
  documentId,
  onVersionSelect,
  currentVersionId,
  initialTab = 'versions',
  onDocumentUpdate,
  currentDocumentStats
}: DMSManagerProps): React.JSX.Element | null {
  const navigate = useAnimatedNavigate();
  const documentAPI = useDocumentAPI();
  const { contacts, fetchContacts } = useContacts();
  const { matters, fetchMatters } = useMatters();
  const { keycloak } = useKeycloak();
  const currentUserInfo = useUserInfo();
  
  // State
  const [selectedTab, setSelectedTab] = useState<SelectedTab>(initialTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [categories, setCategories] = useState<Category[]>([]);
  const [collaboratorRefreshTrigger, setCollaboratorRefreshTrigger] = useState<number>(0);
  
  // Collaborators modal state
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Document collaboration state
  const { state: collaborationState, actions: collaborationActions } = useDocumentCollaboration(documentId);
  
  // User info for permissions
  const userInfo = useUserInfo();

  // Invite user handler
  const handleInviteUser = async (invitation: any) => {
    try {
      console.log('🚀 [DMSManager] handleInviteUser called with:', invitation);
      console.log('🔑 [DMSManager] Keycloak token available:', !!keycloak.token);
      
      await collaborationActions.inviteUser(invitation, keycloak.token);
      console.log('✅ [DMSManager] Invitation sent successfully');
      
      setShowInviteModal(false);
    } catch (error) {
      console.error('❌ [DMSManager] Invitation failed:', error);
      // Error will be handled by the modal
      throw error;
    }
  };
  
  // Real-time WebSocket connection for live collaboration updates
  const socketRef = useRef<any>(null);
  const categoriesFetchInFlightRef = useRef(false);
  
  // Editing state for metadata
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isEditingMatter, setIsEditingMatter] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [isEditingPhase, setIsEditingPhase] = useState(false);
  const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);



  // Load document data
  const {
    document,
    versions,
    analytics,
    isLoading,
    error,
    reloadVersions
  } = useDocumentVersions({ isOpen, documentId });

  // Version actions
  const versionActions = useVersionActions({
    documentId,
    versions,
    onDocumentUpdate,
    onVersionsReload: reloadVersions,
    currentVersionId
  });

  // Auto-initialize collaboration when modal opens
  // (using collaborationActions from above)
  
  // Collaboration is now handled by individual CollaborationManager components

  // WebSocket connections are now handled by individual CollaborationManager components

  useEffect(() => {
    if (!isOpen || !documentId) return;
    setSelectedTab(initialTab);
    
    const initializeCollaboration = async () => {
      try {
        console.log(`🤝 [DMSManager] Auto-initializing collaboration for document: ${documentId}`);
        await collaborationActions.initialize();
        console.log(`✅ [DMSManager] Collaboration auto-initialized successfully`);
      } catch (error) {
        console.warn(`⚠️ [DMSManager] Collaboration auto-initialization failed (will continue without):`, error);
      }
    };
    
    initializeCollaboration();
  }, [isOpen, documentId, initialTab]);


  // Fetch categories function
  const fetchCategories = useCallback(async () => {
    if (!keycloak.token || categoriesFetchInFlightRef.current) {
      return;
    }

    categoriesFetchInFlightRef.current = true;
    try {
      const response = await fetch('/api/v1/dms/documents/categories', {
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
      }
      
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      categoriesFetchInFlightRef.current = false;
    }
  }, [keycloak.token]);

  // Title, Client, Matter, and canonical taxonomy update handlers
  const handleTitleUpdate = async (title: string) => {
    try {
      const nextTitle = title.trim();
      if (!nextTitle) {
        alert('Document title is required');
        return;
      }

      setIsUpdatingDocument(true);
      await documentAPI.updateDocument(documentId, { title: nextTitle });

      await reloadVersions();
      setIsEditingTitle(false);
      onDocumentUpdate?.();
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Failed to update document title');
    } finally {
      setIsUpdatingDocument(false);
    }
  };

  const handleClientUpdate = async (clientId: string | null) => {
    try {
      setIsUpdatingDocument(true);
      await documentAPI.updateDocument(documentId, { client_id: clientId || undefined });
      
      // Reload document data
      await reloadVersions();
      setIsEditingClient(false);
      onDocumentUpdate?.();
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client assignment');
    } finally {
      setIsUpdatingDocument(false);
    }
  };

  const handleMatterUpdate = async (matterId: string | null) => {
    try {
      setIsUpdatingDocument(true);
      await documentAPI.updateDocument(documentId, { matter_id: matterId || undefined });
      
      // Reload document data
      await reloadVersions();
      setIsEditingMatter(false);
      onDocumentUpdate?.();
    } catch (error) {
      console.error('Error updating matter:', error);
      alert('Failed to update matter assignment');
    } finally {
      setIsUpdatingDocument(false);
    }
  };

  const handleCategoryUpdate = async (categoryId: string | null) => {
    try {
      setIsUpdatingDocument(true);
      await documentAPI.updateDocument(documentId, {
        primary_type: categoryId || undefined,
        sub_type: categoryId || undefined,
        classification_method: 'human_review',
      });
      
      // Reload document data
      await reloadVersions();
      setIsEditingCategory(false);
      onDocumentUpdate?.();
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category assignment');
    } finally {
      setIsUpdatingDocument(false);
    }
  };

  const handlePhaseUpdate = async (phaseKey: string | null) => {
    try {
      setIsUpdatingDocument(true);
      await documentAPI.updateDocument(documentId, {
        matter_phase: phaseKey || undefined,
        classification_method: 'human_review',
      });

      await reloadVersions();
      setIsEditingPhase(false);
      onDocumentUpdate?.();
    } catch (error) {
      console.error('Error updating matter phase:', error);
      alert('Failed to update matter phase');
    } finally {
      setIsUpdatingDocument(false);
    }
  };

  // Override version select to include onClose
  const handleVersionSelect = (version: any) => {
    onVersionSelect(version);
    onClose();
  };

  // Override comparison to set tab
  const handleVersionComparison = async () => {
    await versionActions.handleVersionComparison();
    setSelectedTab('comparison');
  };

  const handleCompareWithActive = (versionId: string) => {
    versionActions.handleCompareWithActive(versionId, currentVersionId);
    setSelectedTab('comparison');
  };

  const isWordVersion = (versionId?: string): boolean => {
    const selectedVersion = versionId
      ? versions.find((version) => version.version_id === versionId)
      : versions[0];
    const editorType = (document as any)?.editor_type;
    const mimeType = (selectedVersion?.mime_type || document?.latest_version?.mime_type || '').toLowerCase();
    const originalName = (
      selectedVersion?.original_name ||
      document?.latest_version?.original_name ||
      document?.title ||
      ''
    ).toLowerCase();

    return (
      editorType === 'msword' ||
      mimeType.includes('word') ||
      mimeType.includes('msword') ||
      mimeType.includes('wordprocessingml') ||
      originalName.endsWith('.docx') ||
      originalName.endsWith('.doc')
    );
  };

  // Navigate to the correct editor/launcher for the selected version.
  const handleOpenEditor = (versionId?: string) => {
    if (!documentId) {
      console.error('🚫 [DMSManager] handleOpenEditor called without documentId');
      return;
    }
    
    console.log(`🚀 [DMSManager] Opening orchestrator-based editor:`, {
      documentId,
      versionId: versionId || 'active',
      newRoute: `/dms/editor/${documentId}`,
      timestamp: new Date().toISOString()
    });
    
    const editorUrl = isWordVersion(versionId)
      ? `/documents/${documentId}/word-editor${versionId ? `?version=${versionId}&launch=word` : '?launch=word'}`
      : (versionId
        ? `/dms/editor/${documentId}?versionId=${versionId}`
        : `/dms/editor/${documentId}`);
    
    console.log(`📍 [DMSManager] Navigating to:`, editorUrl);
    
    // Close the modal and navigate to the editor
    onClose();
    navigate(editorUrl);
  };





  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-4">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity lex-overlay-enter" onClick={onClose} />

        {/* Modal panel */}
        <div className="relative w-full max-w-6xl h-[calc(100vh-3rem)] max-h-[920px] flex flex-col bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl lex-modal-enter">
          {/* Header */}
          <div className="flex-shrink-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="h-6 w-6 text-lex-accent" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {document?.title || 'Document History'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Version history and document analytics
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-900 flex-1 min-h-0 overflow-hidden">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent mx-auto"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Loading document history...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto" />
                <p className="mt-2 text-red-600 dark:text-red-400">{error}</p>
              </div>
            ) : (
              <div className="flex h-full min-h-0">
                {/* Left Sidebar - Tabs */}
                <div className="w-64 min-h-0 flex-shrink-0 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
                  <nav className="p-4 space-y-2">
                    <button
                      onClick={() => setSelectedTab('versions')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedTab === 'versions'
                          ? 'bg-lex-accent-soft text-lex-accent'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <DocumentTextIcon className="h-5 w-5" />
                      <span>Version History</span>
                      <span className="ml-auto bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full text-xs">
                        {versions.length}
                      </span>
                    </button>

                    <button
                      onClick={() => setSelectedTab('analytics')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedTab === 'analytics'
                          ? 'bg-lex-accent-soft text-lex-accent'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <ChartBarIcon className="h-5 w-5" />
                      <span>Document Intelligence</span>
                    </button>

                    <button
                      onClick={() => setSelectedTab('processing')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedTab === 'processing'
                          ? 'bg-lex-accent-soft text-lex-accent'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <SparklesIcon className="h-5 w-5" />
                      <span>AI Processing</span>
                    </button>

                    <button
                      onClick={() => setSelectedTab('comparison')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedTab === 'comparison'
                          ? 'bg-lex-accent-soft text-lex-accent'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <ArrowsUpDownIcon className="h-5 w-5" />
                      <span>Compare</span>
                      {versionActions.selectedVersions.length > 0 && (
                        <span className="ml-auto bg-lex-accent text-lex-accent-contrast px-2 py-1 rounded-full text-xs">
                          {versionActions.selectedVersions.length}
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => setSelectedTab('metadata')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedTab === 'metadata'
                          ? 'bg-lex-accent-soft text-lex-accent'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <InformationCircleIcon className="h-5 w-5" />
                      <span>Document Info</span>
                    </button>

                    <button
                      onClick={() => setSelectedTab('workflow')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedTab === 'workflow'
                          ? 'bg-lex-accent-soft text-lex-accent'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <CogIcon className="h-5 w-5" />
                      <span>Lifecycle</span>
                    </button>

                    <button
                      onClick={() => setShowCollaboratorsModal(true)}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <UserGroupIcon className="h-5 w-5" />
                      <span>Collaborators</span>
                    </button>
                  </nav>
                </div>

                {/* Main Content */}
                <div className="flex-1 min-w-0 min-h-0 overflow-x-hidden overflow-y-auto p-6">
                  <div key={selectedTab} className="lex-tab-panel min-w-0 max-w-full">
                  {selectedTab === 'versions' && (
                    <VersionsList
                      versions={versions}
                      currentVersionId={currentVersionId}
                      currentDocumentStats={currentDocumentStats}
                      contacts={contacts}
                      currentUserInfo={currentUserInfo}
                      keycloakTokenParsed={keycloak.tokenParsed}
                      documentId={documentId}
                      documentTitle={document?.title}
                      currentVersion={versions?.[0]?.version_number}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      filterStatus={filterStatus}
                      setFilterStatus={setFilterStatus}
                      selectedVersions={versionActions.selectedVersions}
                      onToggleVersionSelection={versionActions.handleToggleVersionSelection}
                      onClearSelection={versionActions.clearSelectedVersions}
                      onVersionSelect={handleVersionSelect}
                      onDownloadVersion={versionActions.handleDownloadVersion}
                      onPromoteVersion={versionActions.handlePromoteVersion}
                      onDeleteVersion={versionActions.handleDeleteVersion}
                      onBulkDownload={versionActions.handleBulkDownload}
                      onVersionComparison={handleVersionComparison}
                      onCompareWithActive={handleCompareWithActive}
                      onEditVersion={handleOpenEditor}
                      onDocumentUpdate={onDocumentUpdate}
                      onVersionsRefresh={() => {
                        console.log('🔄 [DMSManager] Refreshing versions after checkin');
                        reloadVersions();
                      }}
                    />
                  )}

                  {selectedTab === 'analytics' && analytics && (
                    <DocumentAnalytics
                      analytics={analytics}
                      document={document}
                      versions={versions}
                      onViewTracking={() => setSelectedTab('metadata')}
                    />
                  )}

                  {selectedTab === 'metadata' && document && (
                    <DocumentMetadata
                      document={document}
                      versions={versions}
                      contacts={contacts}
                      matters={matters}
                      categories={categories}
                      isEditingTitle={isEditingTitle}
                      isEditingClient={isEditingClient}
                      isEditingMatter={isEditingMatter}
                      isEditingCategory={isEditingCategory}
                      isEditingPhase={isEditingPhase}
                      isUpdatingDocument={isUpdatingDocument}
                      onTitleUpdate={handleTitleUpdate}
                      onClientUpdate={handleClientUpdate}
                      onMatterUpdate={handleMatterUpdate}
                      onCategoryUpdate={handleCategoryUpdate}
                      onPhaseUpdate={handlePhaseUpdate}
                      setIsEditingTitle={setIsEditingTitle}
                      setIsEditingClient={setIsEditingClient}
                      setIsEditingMatter={setIsEditingMatter}
                      setIsEditingCategory={setIsEditingCategory}
                      setIsEditingPhase={setIsEditingPhase}
                      fetchContacts={fetchContacts}
                      fetchMatters={fetchMatters}
                      fetchCategories={fetchCategories}
                    />
                  )}

                  {selectedTab === 'processing' && versionActions && (
                    <ProcessingPanel
                      isProcessing={versionActions.isProcessing}
                      processingStatus={versionActions.processingStatus}
                      processingNotice={versionActions.processingNotice}
                      onClearProcessingNotice={versionActions.clearProcessingNotice}
                      documentTags={versionActions.documentTags}
                      newTag={versionActions.newTag}
                      setNewTag={versionActions.setNewTag}
                      onProcessDocument={versionActions.handleProcessDocument}
                      onAddTag={versionActions.handleAddTag}
                      onApplyTag={versionActions.handleApplyTag}
                      onRemoveTag={versionActions.handleRemoveTag}
                      document={document}
                      versions={versions}
                      currentVersionId={currentVersionId}
                      runDefinedTerms={versionActions.runDefinedTerms}
                      setRunDefinedTerms={versionActions.setRunDefinedTerms}
                      runExactSearchIndex={versionActions.runExactSearchIndex}
                      setRunExactSearchIndex={versionActions.setRunExactSearchIndex}
                      runCaptionPartiesExtractor={versionActions.runCaptionPartiesExtractor}
                      setRunCaptionPartiesExtractor={versionActions.setRunCaptionPartiesExtractor}
                      persistCoreFacts={versionActions.persistCoreFacts}
                      setPersistCoreFacts={versionActions.setPersistCoreFacts}
                    />
                  )}

                  {selectedTab === 'comparison' && (
                    <ComparisonView
                      comparisonResult={versionActions.comparisonResult}
                      showComparison={versionActions.showComparison}
                      selectedVersions={versionActions.selectedVersions}
                      documentId={documentId}
                      versions={versions}
                    />
                  )}

                  {selectedTab === 'workflow' && (
                    <WorkflowPanel
                      document={document}
                      versions={versions}
                      documentTags={versionActions.documentTags}
                      onOpenCollaborators={() => setShowCollaboratorsModal(true)}
                      onOpenLatestVersion={() => handleOpenEditor(versions[0]?.version_id)}
                      onGoToDocumentInfo={() => setSelectedTab('metadata')}
                      onGoToProcessing={() => setSelectedTab('processing')}
                      onGoToCompare={() => setSelectedTab('comparison')}
                      onGoToVersionHistory={() => setSelectedTab('versions')}
                    />
                  )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <DeleteVersionModal
        isOpen={versionActions.showDeleteConfirm}
        versionToDelete={versionActions.versionToDelete}
        onConfirm={versionActions.confirmDeleteVersion}
        onCancel={versionActions.cancelDeleteVersion}
      />

      <PromoteVersionModal
        isOpen={versionActions.showPromoteConfirm}
        versionToPromote={versionActions.versionToPromote}
        promotionDetails={versionActions.promotionDetails}
        promotionStatus={versionActions.promotionStatus}
        promotionStep={versionActions.promotionStep}
        promotionError={versionActions.promotionError}
        onConfirm={versionActions.confirmPromoteVersion}
        onCancel={versionActions.cancelPromoteVersion}
      />

      <SuccessToast
        isVisible={versionActions.showSuccessToast}
        promotionDetails={versionActions.promotionDetails}
        onClose={() => versionActions.showSuccessToast && (() => {})} // This will need to be fixed in the hook
      />

      {/* Document-Level Collaborators Modal */}
      <CollaborationSettingsDrawer
        isOpen={showCollaboratorsModal}
        onClose={() => setShowCollaboratorsModal(false)}
        documentId={documentId}
        documentTitle={document?.title} // Pass document title for display
        collaborators={collaborationState?.collaborators || []}
        currentUser={collaborationState?.currentUser || undefined}
        isOwner={true} // TODO: Implement proper owner check
        onInviteUser={() => {
          setShowCollaboratorsModal(false);
          setShowInviteModal(true);
        }}
        onUpdateRole={(userId, newRole) => {
          console.log('Update role for user:', userId, 'to:', newRole);
          // Handle role update
        }}
        onRemoveUser={(collaborator) => {
          console.log('Remove user:', collaborator);
          // Handle user removal
        }}
        onUpdatePermissions={(userId, permissions) => {
          console.log('Update permissions for user:', userId, permissions);
          // Handle permission updates
        }}
      />

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInviteUser}
        documentId={documentId}
        documentTitle={document?.title || 'Current Document'}
        currentUser={collaborationState?.currentUser || undefined}
        existingCollaborators={collaborationState?.collaborators || []}
      />
    </div>
  );
}
